// ── ShareWithParentModal ✦ — reusable from Reputation card / Trust ──
//
// Soft glass modal. Generates a /parent/:token link, copy press,
// Web Share API tap-out on mobile, expiry timestamp, and a
// "Revoke link" press that nukes the active snapshot server-side.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Heart, Copy, X, Share, Trash, CheckCircle, Clock, Envelope, ArrowsClockwise } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG_OVERLAY = 'rgba(2,4,16,0.78)';
const PANEL = 'rgba(20,18,40,0.96)';
const PANEL_BORDER = 'rgba(255,255,255,0.10)';
const TEXT = '#F5F5FA';
const TEXT_MUTED = '#9A9ABF';
const BLUE = '#5EA9FF';
const PINK = '#FF7AC2';
const PURPLE = '#8C7BFF';
const FONT_STACK = "'Space Grotesk', 'Inter', system-ui, sans-serif";

const buildShareUrl = (snap) => {
  if (!snap) return '';
  // Prefer absolute window-origin URL so what we show + copy matches what
  // a parent actually taps. The backend returns a relative path when its
  // env doesn't carry REACT_APP_BACKEND_URL — defensive.
  if (snap.url && snap.url.startsWith('http')) return snap.url;
  const base = (BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return `${base}/parent/${snap.token}`;
};

export const ShareWithParentTrigger = ({ onOpen, testid = 'share-parent-trigger', label = 'Share with a parent ✦' }) => (
  <button
    onClick={onOpen}
    data-testid={testid}
    style={{
      padding: '8px 14px', borderRadius: 999,
      border: `1px solid ${BLUE}55`, cursor: 'pointer',
      background: `linear-gradient(135deg, ${BLUE}22, ${PURPLE}1A)`,
      color: TEXT, fontFamily: FONT_STACK, fontSize: 12, fontWeight: 800,
      display: 'inline-flex', alignItems: 'center', gap: 6,
      boxShadow: `0 6px 18px ${BLUE}22`,
    }}
  >
    <Heart size={12} weight="fill" color={PINK} />
    {label}
  </button>
);

const ShareWithParentModal = ({ open, onClose }) => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [emailMode, setEmailMode] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState('');
  // Phase C — crew-controlled dashboard toggles + extend.
  // Loaded from GET /api/crew/me/parent-dashboard; defaults to
  // True/True for any active snapshot (the surfaces are already
  // implicit in the Snapshot — opting OUT is the explicit gesture).
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [showJourney, setShowJourney] = useState(true);
  const [togglesLoaded, setTogglesLoaded] = useState(false);
  const [extending, setExtending] = useState(false);
  const [extendCapped, setExtendCapped] = useState(false);
  const [extendedExpiry, setExtendedExpiry] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // Always POST — endpoint is idempotent (reuses an active snapshot).
      const r = await axios.post(`${API}/crew/me/parent-snapshot`, {});
      setSnap(r.data);
      // Hydrate the dashboard toggle state alongside the snapshot.
      // Tolerated to fail quietly — toggles default ON either way.
      try {
        const t = await axios.get(`${API}/crew/me/parent-dashboard`);
        setShowUpcoming(t.data?.show_upcoming !== false);
        setShowJourney(t.data?.show_journey !== false);
        setTogglesLoaded(true);
      } catch { setTogglesLoaded(true); }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not generate the link');
    } finally { setLoading(false); }
  }, []);

  // Persist a toggle change immediately. Optimistic UI — local state
  // flips first, server writes second; on error we revert and surface.
  const updateToggle = async (key, next) => {
    const before = { show_upcoming: showUpcoming, show_journey: showJourney };
    if (key === 'show_upcoming') setShowUpcoming(next);
    if (key === 'show_journey') setShowJourney(next);
    try {
      await axios.post(`${API}/crew/me/parent-dashboard`, { [key]: next });
    } catch (err) {
      setShowUpcoming(before.show_upcoming);
      setShowJourney(before.show_journey);
      setError(err?.response?.data?.detail || 'Could not update visibility');
    }
  };

  const extend = async () => {
    if (extending) return;
    setExtending(true); setError('');
    try {
      const r = await axios.post(`${API}/crew/me/parent-dashboard/extend`, {});
      const next = r.data?.expires_at;
      if (next) {
        setExtendedExpiry(next.slice(0, 10));
        setExtendCapped(Boolean(r.data?.capped));
        // Sync local snap copy so the expiry chip refreshes.
        setSnap((s) => s ? { ...s, expires_at: next } : s);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not extend the dashboard');
    } finally {
      setExtending(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, load]);

  const url = buildShareUrl(snap);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Copy not supported — long-press the link to copy.');
    }
  };

  const share = async () => {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'A safety snapshot from EventDrop Crew ✦',
          text: 'I wanted to show you the safety side of the platform I\'m using ✦',
          url,
        });
      } catch {}
    } else { copy(); }
  };

  const revoke = async () => {
    setLoading(true); setError('');
    try {
      await axios.delete(`${API}/crew/me/parent-snapshot`);
      setSnap(null);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not revoke');
    } finally { setLoading(false); }
  };

  const sendEmail = async () => {
    const target = emailValue.trim();
    // Light client-side validation — server is authoritative.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      setError('That doesn\'t look like a valid email address.');
      return;
    }
    setEmailSending(true); setError('');
    try {
      const r = await axios.post(`${API}/crew/me/parent-snapshot/email`, {
        parent_email: target,
        message: emailMessage,
      });
      setEmailSentTo(r.data?.parent_email || target);
      setEmailValue('');
      setEmailMessage('');
      // Refresh snap so the modal reflects the (possibly newly-created) token.
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send the email');
    } finally {
      setEmailSending(false);
    }
  };

  if (!open) return null;
  return (
    <div
      data-testid="share-parent-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: BG_OVERLAY, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 460, width: '100%', padding: 22, borderRadius: 22,
          background: PANEL, border: `1px solid ${PANEL_BORDER}`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          fontFamily: FONT_STACK, color: TEXT,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          <div>
            <p style={{ margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: BLUE }}>
              ✦ Parent Snapshot
            </p>
            <p style={{ margin: '4px 0 0', fontWeight: 800, fontSize: 19, color: TEXT, letterSpacing: '-0.005em' }}>
              Send your parent a safe overview
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="share-parent-close"
            style={{ background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer' }}
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <p style={{ margin: '6px 0 14px', color: TEXT_MUTED, fontSize: 13, lineHeight: 1.55 }}>
          A read-only safety summary — your trust badges, upcoming shifts, and how EventDrop
          keeps you safe. No login needed on their side. The link expires after 14 days and
          you can revoke it at any time.
        </p>

        {loading && <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Generating link…</p>}

        {error && (
          <p data-testid="share-parent-error" style={{ color: PINK, fontSize: 12.5 }}>{error}</p>
        )}

        {snap && !loading && (
          <>
            <div
              data-testid="share-parent-url-box"
              style={{
                padding: '10px 12px', borderRadius: 12,
                background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
                color: TEXT, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5,
                wordBreak: 'break-all', lineHeight: 1.5,
              }}
            >
              {url}
            </div>
            <p style={{ margin: '8px 0 0', color: TEXT_MUTED, fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Clock size={11} weight="fill" color={BLUE} />
              Active until {snap.expires_at ? snap.expires_at.slice(0, 10) : '—'}
              {snap.reused ? ' · already active' : ' · just generated'}
            </p>

            {/* ── Phase C — crew-controlled visibility toggles ── */}
            {togglesLoaded && (
              <div
                data-testid="share-parent-toggles"
                style={{
                  marginTop: 16, padding: '14px 14px 6px', borderRadius: 14,
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${PANEL_BORDER}`,
                }}
              >
                <p style={{
                  margin: 0, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: TEXT_MUTED,
                }}>
                  ✦ Choose what you'd like to share
                </p>
                <p style={{ margin: '4px 0 12px', fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>
                  You decide what your parent sees on the dashboard. Flip these any time ✦
                </p>
                {[
                  { key: 'show_upcoming', label: 'Share upcoming opportunities ✦', value: showUpcoming },
                  { key: 'show_journey',  label: 'Share completed event journey ✦', value: showJourney  },
                ].map((t) => (
                  <label
                    key={t.key}
                    data-testid={`share-parent-toggle-${t.key.replace('_', '-')}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, padding: '10px 4px', cursor: 'pointer',
                      borderTop: `1px dashed ${PANEL_BORDER}`,
                    }}
                  >
                    <span style={{ color: TEXT, fontSize: 13, lineHeight: 1.4 }}>{t.label}</span>
                    <span
                      role="switch"
                      aria-checked={t.value}
                      onClick={() => updateToggle(t.key, !t.value)}
                      data-testid={`share-parent-toggle-${t.key.replace('_', '-')}-switch`}
                      style={{
                        width: 38, height: 22, borderRadius: 999,
                        background: t.value
                          ? `linear-gradient(135deg, ${BLUE}, ${PURPLE})`
                          : 'rgba(255,255,255,0.08)',
                        border: `1px solid ${t.value ? BLUE + '88' : PANEL_BORDER}`,
                        position: 'relative', transition: 'background 180ms ease',
                        flexShrink: 0,
                      }}
                    >
                      <span style={{
                        position: 'absolute', top: 2, left: t.value ? 18 : 2,
                        width: 16, height: 16, borderRadius: '50%',
                        background: TEXT,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                        transition: 'left 180ms ease',
                      }} />
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* ── Phase C — extend dashboard by 30 days ── */}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={extend}
                disabled={extending}
                data-testid="share-parent-extend"
                style={{
                  padding: '10px 14px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${BLUE}55`,
                  color: TEXT, fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 700,
                  cursor: extending ? 'wait' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  opacity: extending ? 0.6 : 1,
                }}
              >
                <ArrowsClockwise size={12} weight="bold" color={BLUE} />
                {extending ? 'Extending…' : 'Extend dashboard by 30 days ✦'}
              </button>
              {extendedExpiry && (
                <span
                  data-testid="share-parent-extend-confirmation"
                  style={{
                    fontSize: 11.5, color: TEXT_MUTED,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <CheckCircle size={11} weight="fill" color={BLUE} />
                  Active until {extendedExpiry}
                  {extendCapped && ' ✦ at maximum window'}
                </span>
              )}
            </div>

            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button
                onClick={share}
                data-testid="share-parent-share"
                style={{
                  padding: '10px 16px', borderRadius: 999, border: 'none', cursor: 'pointer',
                  color: '#0A0A20', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 13,
                  background: `linear-gradient(135deg, ${BLUE}, ${PURPLE})`,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  boxShadow: `0 8px 24px ${BLUE}55`,
                }}
              >
                <Share size={12} weight="fill" /> Share link
              </button>
              <button
                onClick={copy}
                data-testid="share-parent-copy"
                style={{
                  padding: '10px 16px', borderRadius: 999, cursor: 'pointer',
                  color: TEXT, fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                  background: 'rgba(255,255,255,0.06)', border: `1px solid ${PANEL_BORDER}`,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {copied
                  ? <><CheckCircle size={12} weight="fill" color={BLUE} /> Copied ✦</>
                  : <><Copy size={12} weight="bold" /> Copy</>}
              </button>
              <button
                onClick={revoke}
                data-testid="share-parent-revoke"
                style={{
                  padding: '10px 14px', borderRadius: 999, cursor: 'pointer',
                  color: TEXT_MUTED, fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
                  background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  marginLeft: 'auto',
                }}
              >
                <Trash size={11} weight="bold" /> Revoke link
              </button>
            </div>

            {/* ── Email to parent ✦ — calm, trust-first, NOT marketing ── */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${PANEL_BORDER}` }}>
              {!emailMode && !emailSentTo && (
                <button
                  onClick={() => setEmailMode(true)}
                  data-testid="share-parent-email-open"
                  style={{
                    padding: '10px 14px', borderRadius: 999, cursor: 'pointer',
                    color: TEXT, fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
                    background: `${BLUE}1A`, border: `1px solid ${BLUE}55`,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Envelope size={12} weight="fill" color={BLUE} />
                  Email to parent ✦
                </button>
              )}

              {emailSentTo && (
                <div
                  data-testid="share-parent-email-success"
                  style={{
                    padding: '10px 12px', borderRadius: 12,
                    background: `${BLUE}10`, border: `1px solid ${BLUE}55`,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <CheckCircle size={14} weight="fill" color={BLUE} />
                  <p style={{ margin: 0, color: TEXT, fontSize: 13, lineHeight: 1.5 }}>
                    Snapshot sent to <b>{emailSentTo}</b> ✦
                  </p>
                </div>
              )}

              {emailMode && !emailSentTo && (
                <div data-testid="share-parent-email-form">
                  <p style={{ margin: 0, fontSize: 11, color: TEXT_MUTED, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                    ✦ Email to parent
                  </p>
                  <p style={{ margin: '4px 0 8px', fontSize: 12, color: TEXT_MUTED, lineHeight: 1.5 }}>
                    A calm, read-only summary lands in their inbox. The link expires in 14 days.
                  </p>
                  <input
                    type="email"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    placeholder="parent@example.com"
                    data-testid="share-parent-email-input"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
                      color: TEXT, fontFamily: FONT_STACK, fontSize: 13.5, outline: 'none',
                    }}
                  />
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder={'Add a short message ✦ (optional, e.g. "Thought I\'d share this with you.")'}
                    data-testid="share-parent-email-message"
                    rows={2}
                    maxLength={240}
                    style={{
                      marginTop: 8, width: '100%', padding: '10px 12px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
                      color: TEXT, fontFamily: FONT_STACK, fontSize: 13, outline: 'none',
                      resize: 'vertical', lineHeight: 1.5,
                    }}
                  />
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      onClick={sendEmail}
                      disabled={emailSending || !emailValue.trim()}
                      data-testid="share-parent-email-send"
                      style={{
                        padding: '10px 16px', borderRadius: 999, border: 'none',
                        cursor: emailSending ? 'wait' : (!emailValue.trim() ? 'not-allowed' : 'pointer'),
                        color: '#0A0A20', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 12.5,
                        background: `linear-gradient(135deg, ${BLUE}, ${PURPLE})`,
                        boxShadow: `0 8px 22px ${BLUE}55`,
                        opacity: emailSending || !emailValue.trim() ? 0.55 : 1,
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <Envelope size={12} weight="fill" />
                      {emailSending ? 'Sending…' : 'Send snapshot'}
                    </button>
                    <button
                      onClick={() => { setEmailMode(false); setEmailValue(''); setEmailMessage(''); }}
                      data-testid="share-parent-email-cancel"
                      style={{
                        padding: '10px 14px', borderRadius: 999, cursor: 'pointer',
                        color: TEXT_MUTED, fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
                        background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
                      }}
                    >Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareWithParentModal;
